import {
  type ComponentType,
  memo,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { type Observable, map } from 'rxjs';
import type { Size } from '@ardoq/graph';
import {
  type Action,
  type ReducerArray,
  action$,
  ofType,
  reducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { clearGraphSelection } from '@ardoq/component-selection';
import {
  BubbleChartCommands,
  BubbleChartRenderingCommands,
  BubbleChartRenderingData,
  BubbleChartViewModelSourceData,
  BubbleChartViewProperties,
  BubbleChartViewSettings,
  BubbleChartViewSettingsControlsHandlers,
  HighlightState,
} from './types';
import {
  panSettle,
  setCenter,
  setFocusState,
  setHoverState,
  setScale,
  setSelectedQuadrant,
} from './actions';
import { bubbleChartOperations } from './operations';
import {
  DEFAULT_CHART_WINDOW_STATE,
  EMPTY_CLIENT_RECT,
  EMPTY_HIGHLIGHT_STATE,
} from './consts';
import { INITIAL_RENDERING_DATA_STATE } from './streams/consts';
import { BubbleChartView } from './BubbleChartView';

type ConnectedBubbleChartProps = Omit<
  BubbleChartViewProperties,
  keyof BubbleChartRenderingData
>;

export type BubbleChartRenderingLayerProps<TSettingsControlsData> =
  ConnectedBubbleChartProps & {
    viewInstanceId: string;
    sourceData: BubbleChartViewModelSourceData;
    viewSettingsForRenderingData: BubbleChartViewSettings;
    bubbleChartCommands: BubbleChartCommands;
    settingsControlsHandlers?: BubbleChartViewSettingsControlsHandlers<TSettingsControlsData>;
    onRenderingDataChange?: (
      previousState: BubbleChartRenderingData,
      currentState: BubbleChartRenderingData
    ) => void;
    BubbleChartComponent?: ComponentType<BubbleChartViewProperties>;
  };

type RenderingDataLayerInputs = {
  viewInstanceId: string;
  sourceData: BubbleChartViewModelSourceData;
  viewSettings: BubbleChartViewSettings;
  bubbleChartCommands: BubbleChartCommands;
};

type RenderingDataLayerState = RenderingDataLayerInputs & {
  clientRect: Size;
  hoverState: HighlightState;
  focusState: HighlightState;
  renderingData: BubbleChartRenderingData;
};

type RenderingDataLayerAction =
  | { type: 'sourceDataReset'; sourceData: BubbleChartViewModelSourceData }
  | { type: 'viewSettingsChanged'; viewSettings: BubbleChartViewSettings }
  | { type: 'commandsChanged'; bubbleChartCommands: BubbleChartCommands }
  | { type: 'clientRectChanged'; clientRect: Size }
  | {
      type: 'contentContainerChanged';
      getContentContainer: () => HTMLDivElement | null;
    }
  | { type: 'interactionActionDispatched'; action: Action<unknown> };

const rebuildRenderingData = (
  state: RenderingDataLayerState
): RenderingDataLayerState => ({
  ...state,
  renderingData: bubbleChartOperations.buildRenderingData(state.renderingData, [
    state.clientRect,
    state.sourceData,
  ]),
});

const updateInteractiveStates = (
  state: RenderingDataLayerState
): RenderingDataLayerState => ({
  ...state,
  renderingData: bubbleChartOperations.updateInteractiveStates(
    state.renderingData,
    [state.hoverState, state.focusState]
  ),
});

const applyHoverState = (
  state: RenderingDataLayerState,
  hoverState: HighlightState
): RenderingDataLayerState => updateInteractiveStates({ ...state, hoverState });

const applyFocusState = (
  state: RenderingDataLayerState,
  focusState: HighlightState
): RenderingDataLayerState => updateInteractiveStates({ ...state, focusState });

const clearFocusState = (
  state: RenderingDataLayerState
): RenderingDataLayerState => applyFocusState(state, EMPTY_HIGHLIGHT_STATE);

const layerReducers: ReducerArray<RenderingDataLayerState> = [
  reducer(setHoverState, applyHoverState),
  reducer(setFocusState, applyFocusState),
  reducer(clearGraphSelection, clearFocusState),
];

const renderingDataReducers: ReducerArray<BubbleChartRenderingData> = [
  reducer(setScale, bubbleChartOperations.handleSetScale),
  reducer(setCenter, bubbleChartOperations.handleSetCenter),
  reducer(panSettle, bubbleChartOperations.panSettle),
  reducer(setSelectedQuadrant, bubbleChartOperations.setSelectedQuadrant),
];

const interactionActionCreators = [
  ...layerReducers,
  ...renderingDataReducers,
].flatMap(({ trigger }) => ('actions' in trigger ? trigger.actions : []));

const findReducer = <State,>(reducers: ReducerArray<State>, type: string) =>
  reducers.find(
    ({ trigger }) =>
      'actions' in trigger &&
      trigger.actions.some(actionCreator => actionCreator.type === type)
  );

const applyInteractionReducer = (
  state: RenderingDataLayerState,
  { type, payload }: Action<unknown>
): RenderingDataLayerState => {
  const layerReducer = findReducer(layerReducers, type);
  if (layerReducer) {
    return layerReducer(state, payload);
  }
  const renderingDataReducer = findReducer(renderingDataReducers, type);
  return renderingDataReducer
    ? {
        ...state,
        renderingData: renderingDataReducer(state.renderingData, payload),
      }
    : state;
};

const updateRenderingData = (
  state: RenderingDataLayerState,
  action: RenderingDataLayerAction
): RenderingDataLayerState => {
  const { renderingData } = state;
  switch (action.type) {
    case 'sourceDataReset':
      return rebuildRenderingData({ ...state, sourceData: action.sourceData });
    case 'clientRectChanged':
      return rebuildRenderingData({ ...state, clientRect: action.clientRect });
    case 'viewSettingsChanged':
      return {
        ...state,
        viewSettings: action.viewSettings,
        renderingData: bubbleChartOperations.updateViewSettings(
          renderingData,
          action.viewSettings
        ),
      };
    case 'commandsChanged':
      return {
        ...state,
        bubbleChartCommands: action.bubbleChartCommands,
        renderingData: bubbleChartOperations.updateCommands(
          renderingData,
          action.bubbleChartCommands
        ),
      };
    case 'contentContainerChanged':
      return {
        ...state,
        renderingData: bubbleChartOperations.updateGetContentContainer(
          renderingData,
          action.getContentContainer
        ),
      };
    case 'interactionActionDispatched':
      return applyInteractionReducer(state, action.action);
  }
};

const initRenderingData = (
  inputs: RenderingDataLayerInputs
): RenderingDataLayerState => {
  const { viewInstanceId, sourceData, viewSettings, bubbleChartCommands } =
    inputs;
  const mountActions: RenderingDataLayerAction[] = [
    { type: 'commandsChanged', bubbleChartCommands },
    { type: 'sourceDataReset', sourceData },
    { type: 'viewSettingsChanged', viewSettings },
    {
      type: 'interactionActionDispatched',
      action: setHoverState(EMPTY_HIGHLIGHT_STATE),
    },
    {
      type: 'interactionActionDispatched',
      action: setScale(DEFAULT_CHART_WINDOW_STATE),
    },
    // The streamed layer pushed source data and view settings again on mount;
    // only this second build reads the real domain-relevant view settings.
    { type: 'sourceDataReset', sourceData },
    { type: 'viewSettingsChanged', viewSettings },
  ];
  return mountActions.reduce(updateRenderingData, {
    ...inputs,
    clientRect: EMPTY_CLIENT_RECT,
    hoverState: EMPTY_HIGHLIGHT_STATE,
    focusState: EMPTY_HIGHLIGHT_STATE,
    renderingData: { ...INITIAL_RENDERING_DATA_STATE, viewInstanceId },
  });
};

const getRenderingDataLayerAction$ = (
  viewInstanceId: string
): Observable<RenderingDataLayerAction> =>
  action$.pipe(
    withNamespace(viewInstanceId),
    ofType(...interactionActionCreators),
    map((action): RenderingDataLayerAction => ({
      type: 'interactionActionDispatched',
      action,
    }))
  );

const BubbleChartRenderingLayerComponent = <TSettingsControlsData,>({
  viewInstanceId,
  sourceData,
  viewSettingsForRenderingData,
  bubbleChartCommands,
  settingsControlsHandlers,
  onRenderingDataChange,
  BubbleChartComponent = BubbleChartView,
  ...connectedComponentProps
}: BubbleChartRenderingLayerProps<TSettingsControlsData>) => {
  const [state, dispatch] = useReducer(
    updateRenderingData,
    {
      viewInstanceId,
      sourceData,
      viewSettings: viewSettingsForRenderingData,
      bubbleChartCommands,
    },
    initRenderingData
  );

  if (state.sourceData !== sourceData) {
    dispatch({ type: 'sourceDataReset', sourceData });
  }
  if (state.viewSettings !== viewSettingsForRenderingData) {
    dispatch({
      type: 'viewSettingsChanged',
      viewSettings: viewSettingsForRenderingData,
    });
  }
  if (state.bubbleChartCommands !== bubbleChartCommands) {
    dispatch({ type: 'commandsChanged', bubbleChartCommands });
  }

  const viewCommands = useMemo(
    (): BubbleChartCommands & BubbleChartRenderingCommands => ({
      ...bubbleChartCommands,
      setClientRect: clientRect =>
        dispatch({ type: 'clientRectChanged', clientRect }),
      setContentContainer: getContentContainer =>
        dispatch({ type: 'contentContainerChanged', getContentContainer }),
    }),
    [bubbleChartCommands]
  );

  useEffect(() => {
    const subscription =
      getRenderingDataLayerAction$(viewInstanceId).subscribe(dispatch);
    return () => subscription.unsubscribe();
  }, [viewInstanceId]);

  const { renderingData } = state;

  const previousRenderingDataRef = useRef<BubbleChartRenderingData | null>(
    null
  );

  const reportRenderingData = useEffectEvent(
    (renderingData: BubbleChartRenderingData) => {
      const previousRenderingData = previousRenderingDataRef.current ?? {
        ...INITIAL_RENDERING_DATA_STATE,
        viewInstanceId,
      };
      previousRenderingDataRef.current = renderingData;

      if (settingsControlsHandlers) {
        const { getSettingsControlsData, publishSettingsControlsData } =
          settingsControlsHandlers;

        const settingsControlsData = getSettingsControlsData(
          previousRenderingData,
          renderingData
        );

        if (settingsControlsData) {
          publishSettingsControlsData(settingsControlsData);
        }
      }

      onRenderingDataChange?.(previousRenderingData, renderingData);

      renderingData.viewInterface?.trackingCallbacks?.onViewModelReady?.();
    }
  );

  useEffect(() => {
    reportRenderingData(renderingData);
  }, [renderingData]);

  return (
    <BubbleChartComponent
      {...connectedComponentProps}
      {...renderingData}
      bubbleChartCommands={viewCommands}
    />
  );
};

/** The cast preserves the generic parameter that `memo` erases. */
const BubbleChartRenderingLayer = memo(
  BubbleChartRenderingLayerComponent
) as typeof BubbleChartRenderingLayerComponent;

export { BubbleChartRenderingLayer };
